import { CountryCode, Nullish } from '@glow/common'
import {
  HousingInformation,
  I18nTextProps,
  InvoicingStatus,
  OrderType,
  RoutePointType,
  UserAccessKey,
  SessionUser as UserBase
} from '@glow/entity-types'
import type { IconName } from '@glow/ui-components'
import i18next from 'i18next'
import { List, Map, Set } from 'immutable'
import { DateTime } from 'luxon'
import { AnyData } from '../actions/creators/baseHelpers'
import { Customer, CustomerIdType } from '../domain/customer'
import { PresetDimension, ValueAddedService } from '../pages/instant/bookingOrderTypes'
import { TemplateShipment } from '../pages/planner/recurring/recurringOrderTypes'
import { IError } from '../selectors/optimizeJobResultsSelector'
import { EventType } from '../utils/consignmentEvent'
import { ConsignmentState } from '../utils/consignmentState'
import {
  DepartmentGroupType,
  DepartmentType,
  DepartmentTypes,
  OrderBillingInfo,
  OrderBillingInfoProps
} from '../utils/departmentAndDepartmentGroupUtils'
import { ImmutableMap } from './immutableTypes'

interface RoutePointBaseProps {
  id: number
  type: RoutePointType
  consignmentId: number | null
  arrival: string
  arrivalCached: DateTime
  arrivalDateIsoCached: string | null
  adjustedArrival: string
  lat: number
  lng: number
  ignore: boolean
  slotId: SlotIdType
  waitingTimeSec: number
  tripNumber?: number
  endTime?: ISODateTimeString
  departmentId: DepartmentIdType
  equalityKey: string
  orderId?: number
  stopNumber: number | null
  plannedStopId?: PlannedStopIdType
}

export interface RoutePointSuggestionProps {
  id: number
  type: RoutePointType
  consignmentId: ConsignmentIdType | null
  arrival: string
  adjustedArrival?: string
  adjustedArrivalCached?: DateTime
  arrivalCached: DateTime
  lat?: number | null
  lng?: number | null
  slotId: SlotIdType
  sortIndex: number
  onCar: boolean
  previousArrival: string
  isFlex: boolean
  isForcedOrder: boolean
  isAssigned?: boolean
  waitingTimeSec: number
  equalityKey: string
  stopNumber?: number | null
  tripNumber?: number
  endTime?: string
  loadCarrierId?: string
  plannedStopId?: PlannedStopIdType
  createdBy?: string | null
  createdAt?: string | null
  createdByName?: string | null
}

export type PlannedStopIdType = number

export interface PlannedStopProps {
  id: PlannedStopIdType
  routeId: SlotIdType
  serviceTime: number
  visitEarliest: ISODateTimeString
  visitLatest: ISODateTimeString
  locationId: PlannedStopLocationIdType
  departmentId: DepartmentIdType
}

export interface PlannedStop extends ImmutableMap<PlannedStopProps> {}

export interface RoutePointSuggestion extends ImmutableMap<RoutePointSuggestionProps> {}

export interface VanillaRoutePoint extends RoutePointBaseProps {
  pickupLocation: string
  deliveryLocation: string
}

export interface OtherRoutePointWithAddress extends RoutePointBaseProps {
  depotAddress: string
}

export interface OtherRoutePointWithDuration extends RoutePointBaseProps {
  duration: string
}

export type IdType = number
export type Integer = number
export type SlotIdType = number
export type DepartmentIdType = number
export type CustomerRefType = string
export type OrderIdType = number
export type ConsignmentIdType = number
export type ConsignmentEventId = number
export type ConsignmentEventActionId = number
export type CourierLocationIdType = number
export type PackageIdType = string
export type UnitIdType = number
export type VehicleIdType = number

export type ISODateString = string
export type ISODateTimeString = string
export type ISOTimeString = string

export type OtherRoutePoint = OtherRoutePointWithAddress | OtherRoutePointWithDuration
export type RoutePoint = ImmutableMap<OtherRoutePoint | VanillaRoutePoint>
export type RoutePointOrSuggestion = ImmutableMap<
  ((OtherRoutePoint | VanillaRoutePoint) & Partial<RoutePointSuggestionProps>) | RoutePointSuggestionProps
>

export type ServiceMatrixIdType = number
export type Second = number
export type Minute = number
export type CustomerAlystraId = string
export type DepartmentAlystraId = string
export type DeliveryMetaDataId = number

export interface EntityProps {
  id: number
  isAssignable?: boolean
  isEditable?: boolean
  isDeletable?: boolean
  isSelectable?: boolean
  isDeletedShipmentCopyable?: boolean
  isDeletedShipmentRestorable?: boolean
  isDeletedShipment?: boolean
  isDuplicable?: boolean
  loadCarrierId?: string
}

export interface Entity<T> extends ImmutableMap<EntityProps & T> {}

export interface I18nText extends ImmutableMap<I18nTextProps> {}

export interface ConsignmentProps {
  arrivedAtDip: boolean | null
  arrivedAtHub: boolean | null
  carrierTotalPrice: number | null
  collectedEvent?: boolean
  courierId?: UnitIdType
  courierName?: string
  createdAt: ISODateTimeString | null
  createdEvent?: boolean
  custAlystraId: string
  custAlystraSubcustomer: string
  custContact?: string
  custId: CustomerIdType
  custName: string
  customer?: Customer
  customerId: CustomerIdType
  customerRef?: CustomerRefType
  customerTotalPrice: number | null
  deliveredEvent?: boolean
  deliveryAddress: string | null
  deliveryAddressLat: number
  deliveryAddressLng: number
  deliveryAttempts: number | null
  deliveryContactPerson?: string
  deliveryCountry: string
  deliveryDisplayType: string
  deliveryEmail?: string | null
  deliveryInstructions: string
  deliveryLat: number | null
  deliveryLng: number | null
  deliveryName: string
  deliveryPhone: string
  deliveryPupUnitId: string | null
  deliverySecondPhone?: string | null
  deliverySms: boolean
  deliveryTimeEarliest: ISODateTimeString
  deliveryTimeEarliestCached: DateTime
  deliveryTimeLatest: ISODateTimeString
  deliveryTimeLatestCached: DateTime
  deliveryZipArea: string
  deliveryZipCode: string
  departmentId: DepartmentIdType
  departmentType: DepartmentType
  driverUserId?: number
  emailId?: string
  estimatedArrivalTime?: ISODateTimeString
  estimatedPickupArrivalTime?: ISODateTimeString
  externalId: string
  failed: boolean | null
  groupId?: string
  hasPreAdviceSetting: boolean | null
  heightUnit: string
  heightValue: number
  id: ConsignmentIdType
  custInformation: string
  label?: string
  lengthUnit: string
  lengthValue: number
  loadCarrierId?: string
  loadingMetersUnit: string
  loadingMetersValue: number
  location: string | null
  multilegId?: number
  multilegType?: string
  name: string
  offeredTimeLatest: ISODateTimeString | null
  orderedDeliveryDate?: DateTime
  orderId: OrderIdType
  orderInvoicingStatus: boolean | null
  orderInvoicingStatusNew?: InvoicingStatus
  originalDeliveryAddress: string | null
  originalDeliveryCountry: string
  originalDeliveryTimeEarliest: ISODateTimeString | null
  originalDeliveryTimeLatest: ISODateTimeString | null
  originalDeliveryZipArea: string
  originalDeliveryZipCode: string
  originalPickupAddress: string | null
  originalPickupCountry: string
  originalPickupZipArea: string
  originalPickupZipCode: string
  packageId: string
  packageTypeCode: string | null
  pickupAddress: string | null
  pickupAddressLat: number
  pickupAddressLng: number
  pickupContactPerson?: string
  pickupCountry: string
  pickupEmail?: string | null
  pickupInstructions: string
  pickupLat: number | null
  pickupLng: number | null
  pickupName: string
  pickupPhone: string
  pickupPupUnitId: string | null
  pickupSecondPhone?: string | null
  pickupTimeEarliest: ISODateTimeString
  pickupTimeEarliestCached: DateTime
  pickupTimeLatest: ISODateTimeString
  pickupTimeLatestCached: DateTime
  pickupZipArea: string
  pickupZipCode: string
  plannedDeliveryDate?: DateTime
  preAdvices: number | null
  preAdviceScheduledDate: DateTime | null
  preAdviceStatus: string | null
  preplannedToUnit: string | null
  presetDimension: PresetDimension
  quantity?: number
  recipientRef?: CustomerRefType
  resolvedAddressDeliveryId: number | null
  resolvedAddressPickupId: number | null
  returnToSender: boolean | null
  scanToCreatePackages: boolean
  serviceCode: string
  serviceLevel: 'premium' | 'standard' | 'none'
  shipmentId: string
  slotId: SlotIdType | null
  slotName?: string
  source?: string
  specificationDescription: string
  specificationSize?: string
  state: ConsignmentState
  totalloadingMetersUnit: string
  totalloadingMetersValue: number
  totalPalletFootprintsValue: number
  totalVolumeUnit: string
  totalVolumeValue: number
  totalWeightUnit: string
  totalWeightValue: number
  bookedTotalVolumeUnit: string
  bookedTotalVolumeValue: number
  bookedTotalWeightUnit: string
  bookedTotalWeightValue: number
  type: string | null
  unresolvedAddressDeliveryId: number | null
  unresolvedAddressPickupId: number | null
  vasCodes: List<string>
  vasDetails: List<ValueAddedService>
  vehicleType?: string
  volumeUnit: string
  volumeValue: number
  bookedVolumeUnit: string
  bookedVolumeValue: number
  bookedWeightUnit: string
  bookedWeightValue: number
  weightUnit: string
  weightValue: number
  widthUnit: string
  widthValue: number
  xmlServiceCode: string
  productionTimeEarliest: ISODateTimeString
  productionTimeLatest: ISODateTimeString
  palletFootprintsValue: number | null
  priceUpdateId: number | null
  deptName?: string
  externalInvoiceNumber?: string
  internalInvoiceNumber?: string
  invoicedOn: Nullish<ISODateString>
}

export interface Consignment extends ImmutableMap<ConsignmentProps> {}

export interface ShipmentProps extends ConsignmentProps {
  id: number
  noOfPackages: number
  consignments: List<Consignment>
  consignmentIds: Set<ConsignmentIdType>
  priority?: string
  groupId?: string
  groupName?: string
  groupOrder?: number
  paidTogether?: boolean
  departmentIdOverridden?: boolean
  multilegId?: number
  multilegSeqNumber?: number
  deptName?: string
  vehicleType?: string
  deletedBy: string
  deletedByName: string
  reason: string
  courierId: number
  deliveredTime: DateTime
  actualPickupTime: DateTime
  etaSmsSentAt: ISODateTimeString
  note: string | null | undefined
  isReturned: boolean
  shipmentState?: ConsignmentState
  deletedOrderDetailsPresent?: boolean
  isMultileg?: boolean
  isLastLeg?: boolean
  deviationType?: string
  deviationCode?: string
  parentOrderId?: number | null
  deviation?: string
  isDeletedShipment?: boolean
  serviceCodeText: string
  vasCodesTexts: string
  goodsTypesText?: string
  pickupDurationSeconds: string
  deliveryDurationSeconds: string
  pickupPreparationSeconds: string
  deliveryPreparationSeconds: string
  packagesArrivedAtDip: ShipmentSyncedStatus | null
  packagesCollected: ShipmentSyncedStatus | null
  packagesDelivered: ShipmentSyncedStatus | null
  type: OrderType | null
  courierName?: string
  unitId?: UnitIdType
  isCredited: boolean
  manualInvoiceInfo: string | null
  failedPriceCalculation?: FailedCalculation | null
  failedPriceList?: string
  priceFailureReason?: string
  priceFailureDetail?: string
  preplannedTemplateSlot?: TemplateSlot
  selfBillingUnitId?: string
  vendorInvoice?: Invoice
  customerInvoice?: Invoice
  /** Fetched from {@link PriceUpdate} entity */
  netCustomerPrice: number | null
  /** Fetched from {@link PriceUpdate} entity */
  netResourcePrice: number | null
  customerCurrency: string | null
  resourceCurrency: string | null
  invoicedOn: Nullish<ISODateString>
  accountingErrors?: List<string> | null
  isUnassigned?: boolean
  isReturnDeliveryLegFailed?: boolean
}

export interface Invoice
  extends ImmutableMap<{
    externalSystemId: string
    linkToInvoice: string
    processedAt: string
    transactionNumber: string
  }> {}

export interface FailedCalculation extends ImmutableMap<FailedCalculationProps> {}

export interface FailedCalculationProps {
  priceRole?: string
  priceListId?: number
  priceListName?: string
  articleCode?: string
  articleType?: string
  mainArticle?: boolean
  reason?: PriceFailureReason
  attribute?: string
  attributeValue?: string
  failedExpression?: string
  details?: string
}

export type PriceFailureReason = 'missingAttributeValue' | 'matrixLookupMiss' | 'invalidExpression' | 'scriptError'

export type ShipmentSyncedStatus = 'no' | 'partial' | 'yes'

export interface Shipment extends ImmutableMap<ShipmentProps> {}

export interface SearchShipmentProps extends ShipmentProps {
  driverName?: string
  deviationType?: string
  deviationText?: string
  addressWashRecipientId?: string | null
  addressWashAddressId?: string | null
}

export interface SearchShipment extends ImmutableMap<SearchShipmentProps> {}

export type DeliveryDisplayType = 'customized' | 'instant'
export type DepartmentServiceLevel = 'standard' | 'none'

export interface DepartmentAutoDispatchSettingsProps {
  isEnabled: boolean
  minutesAfterOrderArrival: number | '' | null
  startTime: string | null
}

export interface DepartmentAutoDispatchSettings extends ImmutableMap<DepartmentAutoDispatchSettingsProps> {}

export interface DepartmentProps {
  address?: string
  alystraId: string
  areaId: number
  autoInvoiceTime: string | null
  autoInvoiceOffsetDays: number | null
  autoRecurrentTime?: string
  autoRouteCreationTime?: string
  autoRouteOptimizationTime?: string
  country: string
  deliveryDisplayType: DeliveryDisplayType
  departmentType: DepartmentTypes
  description: string | null
  emailAddresses: List<string> | null
  id: DepartmentIdType
  lat: number
  lng: number
  name: string
  npsId: string | null
  serviceLevel: DepartmentServiceLevel | null
  timeZone: string
  unitNumber: string | null
  defaultAlystraUnitId?: string
  zipArea: string | null
  zipCode: string | null
  orderBillingInfos: List<OrderBillingInfo> | null
  autoDispatchSettings?: DepartmentAutoDispatchSettings | null
  terminalName: string | null
  delegatedDepartment: string | null
  disableSmsOnSlot: boolean
  defaultTemplate: number | null
}

export interface DepartmentFormValues {
  address: string
  alystraId: string
  enableAutoInvoice?: boolean
  autoInvoiceTime: string | null
  autoInvoiceOffsetDays: number | null
  autoInvoiceEnabled?: boolean
  autoRecurrentTime: string
  autoRouteCreationTime: string
  autoRouteOptimizationTime: string
  country: string
  deliveryDisplayType: string
  department?: number
  departmentType: string
  description: string
  emailAddress: string
  lat: number
  lng: number
  name: string
  npsId: string
  serviceLevel: string
  timeZone: string
  unitNumber: string
  defaultAlystraUnitId: string
  zipArea: string
  zipCode: string
  orderBillingInfos: OrderBillingInfoProps[] | null
  autoDispatchSettings: DepartmentAutoDispatchSettingsProps
  terminalName?: string
  delegatedDepartment: string | null
  disableSmsOnSlot: boolean
  departmentGroupRegionId: string
  hasRegionOptions: boolean
}

export interface PlannedStopLocationsFormValues {
  plannedStopLocations: PlannedStopLocationProps[] | null
}

export type RouteLabelFormValue = { id?: RouteLabelIdType; category: string; name: string }

export type ManageDepartmentFormValues = DepartmentFormValues &
  PlannedStopLocationsFormValues & {
    routeLabels: RouteLabelFormValue[]
    newRouteLabel: { category: string; name: string }
  }

export type PlannedStopLocationIdType = number

export interface PlannedStopLocationProps {
  id?: PlannedStopLocationIdType
  name: string
  address: string
  postalCode: string
  postalArea: string
  country: string
  coordinates: {
    lat: number
    lng: number
  }
  departmentId?: DepartmentIdType
  isTerminal: boolean
}

export type RouteLabelIdType = number

export type RouteLabelProps = {
  id: RouteLabelIdType
  name: string
  category: string
  departmentId: DepartmentIdType
}

export interface RouteLabel extends ImmutableMap<RouteLabelProps> {}

export interface PlannedStopLocation extends ImmutableMap<PlannedStopLocationProps> {}

export interface Department extends ImmutableMap<DepartmentProps> {}

export type Address = {
  address?: string
  postalCode: string
  postalArea: string
  countryCode: string
}

export interface DepartmentPropsWithNumberOfCourierAndPlanner extends DepartmentProps {
  numberOfPlanners: number | string
  numberOfDepartmentGroups: number
}

export interface DepartmentWithNumberOfCourierAndPlanner
  extends ImmutableMap<DepartmentPropsWithNumberOfCourierAndPlanner> {}

export const isH2Department = (department?: Department): boolean => department?.get('departmentType') === 'h2' || false

export const isHdDepartment = (department?: Department): boolean => department?.get('departmentType') === 'hd' || false

export const isCourierDepartment = (department?: Department): boolean => department?.get('departmentType') === 'courier'

export const isParcelDepartment = (department?: Department): boolean =>
  department?.get('departmentType') === 'parcel' || false

export const isNorwegianParcelDepartment = (department?: Department): boolean =>
  isParcelDepartment(department) && department?.get('country')?.toUpperCase() === CountryCode.NO

export const isPostenDepartment = (department?: Department): boolean =>
  department?.get('departmentType') === 'posten' || false

export const orderTypeSupportsPreAdvice = (orderType: OrderType | null): boolean =>
  orderType === 'HD' || orderType === 'Baggage'

export const orderTypeNotifiesRecipient = (orderType: OrderType | null | undefined): boolean =>
  orderType === 'HD' || orderType === 'H2' || orderType === 'Baggage'

export interface IBaseConsignmentEvent {
  id: ConsignmentEventId
  type: string
  consignmentId: ConsignmentIdType
  departmentId?: DepartmentIdType
  orderId: number
  createdAt: ISODateTimeString
  createdBy: string
  name?: string
  data?: AnyData
  eventTime: ISODateTimeString
  packageId?: string
  slotId?: SlotIdType
  role?: string
  custId?: CustomerIdType
}

export interface BaseConsignmentEvent extends ImmutableMap<IBaseConsignmentEvent> {}

export interface IConsignmentEvent extends IBaseConsignmentEvent {
  type: EventType
}

export interface ConsignmentEvent extends ImmutableMap<IConsignmentEvent> {}

export const isH2Order = (type?: string | null): boolean => type === 'H2'

export const isHDOrder = (type?: string | null): boolean => type === 'HD'

export const isHdOrder = (order?: Order): boolean =>
  order?.get('type') === 'HD' || order?.get('source') === 'Happy Flow'

export const isHdTemplateShipment = (shipment?: TemplateShipment): boolean => shipment?.orderType === 'HD'

export const isBaggageOrder = (order?: Order): boolean =>
  order?.get('type') === 'Baggage' || order?.get('source') === 'BaggageSolutions'

export const isBaggageTemplateShipment = (shipment?: TemplateShipment): boolean => shipment?.orderType === 'Baggage'

interface ADRRestriction
  extends ImmutableMap<{
    type: string
    value: string
  }> {}

export type DangerousGoodsProps = {
  hazardClass: string
  hazardIdentifier: string
  hazardPoints: number
  id: number
  netWeightKg: number
  orderId: number
  packingGroup: string
  regulationCode: string
  restrictions: ADRRestriction[]
  technicalName: string
}

export interface DangerousGoods extends ImmutableMap<DangerousGoodsProps> {}

export interface Order
  extends ImmutableMap<{
    id: OrderIdType
    shipmentId: string
    invoiceInfo: string
    manualInvoiceInfo: string
    customerId: CustomerIdType
    departmentIdOverridden: boolean
    departmentId: number
    groupOrder?: number
    groupId?: string
    groupName?: string
    paidTogether?: boolean
    multilegId?: number
    multilegSeqNumber?: number
    vehicleType?: string
    emailId?: string
    createdEvent?: boolean
    collectedEvent?: boolean
    deliveredEvent?: boolean
    orderNote: string | undefined
    source: string | undefined
    customerContact: string | undefined
    pickupTimeEarliest?: ISODateTimeString
    type: OrderType
    dangerousGoodss?: DangerousGoods[]
    parentId: OrderIdType | null
    invoiceSplit?: string
    preplannedTemplateSlotId?: number
    standaloneInvoice?: boolean
    priceUpdateId: number | null
  }> {}

export interface OrderNote
  extends ImmutableMap<{
    id: number
    note: string
    orderId: OrderIdType
    type: string
    createdAt: ISODateTimeString
    updatedAt: ISODateTimeString
    updatedAtEpoch: number
  }> {}

export type TextIdType = number

export interface ServiceMatrixTextProps {
  dk: string
  en: string
  fi: string
  fr: string | null
  nb: string
  sv: string
  groupKey: string
  id: TextIdType
  name: string
}

export interface ServiceMatrixText extends ImmutableMap<ServiceMatrixTextProps> {}

export type UpsalesRuleId = number
export type UpsalesRulesType = 'SERVICE_UPGRADE' | 'SHORTER_TIME_WINDOW'

export interface UpsalesRulesProps {
  id: UpsalesRuleId
  name: string
  type: UpsalesRulesType
  country: string
  zipCodeRanges: List<ZipCodeRange>
  extraSettings: UpsalesRulesExtraSettings | null
  departments: List<DepartmentIdType> | null
  customers: List<CustomerIdType> | null
  isEnabled: boolean
}

export interface ZipCodeRange
  extends ImmutableMap<{
    from: string
    to: string
    type: ZipCodeRangeType
  }> {}

export type ZipCodeRangeType = 'INCLUDE' | 'EXCLUDE'

export type ZipCodeInput = 'RANGE' | 'EXACT'

export type TemplateRouteRuleId = number

export type TemplateRouteRuleType = 'POSTAL_CODE'

export interface TemplateRoutePostalCodeRange
  extends ImmutableMap<{
    from: string
    to: string
    type: ZipCodeRangeType
  }> {}

export interface TemplateRouteRuleDetails
  extends ImmutableMap<{
    postalCodeRanges: List<TemplateRoutePostalCodeRange>
    excludedServiceCodes: List<string> | null
  }> {}

export interface TemplateRouteRuleProps {
  id: TemplateRouteRuleId
  name: string
  type: TemplateRouteRuleType
  details: TemplateRouteRuleDetails
  departmentId: DepartmentIdType
  templateRouteIds: List<number> | null
  recurringRouteIds: List<number> | null
}

export interface TemplateRouteRule extends ImmutableMap<TemplateRouteRuleProps> {}

export interface RoutePostalCodeRuleProps {
  id?: number
  routeId?: number
  from: string
  to: string
  type: ZipCodeRangeType
}

export interface RoutePostalCodeRule extends ImmutableMap<RoutePostalCodeRuleProps> {}

export interface ConnectedTemplateRouteRuleProps {
  id: TemplateRouteRuleId
  name: string
}

export interface UpsalesRulesExtraSettings
  extends ImmutableMap<{
    shorterTimeWindow?: ShorterTimeWindowExtraSettings
  }> {}

export interface ShorterTimeWindowExtraSettings
  extends ImmutableMap<{
    windowOnlyInEvening: boolean
    oneHour: boolean
    twoHours: boolean
    threeHours: boolean
  }> {}

export interface UpsalesRules extends ImmutableMap<UpsalesRulesProps> {}

export type FlexType = 'UNATTENDED_FLEX' | 'EXTENDED_FLEX'

export interface ServiceMapping {
  id: ServiceMatrixIdType
  customerAlystraId: CustomerAlystraId
  pickuptimeinsecs: Second
  timeWindowInMinutes: Minute
  deliverytimeinsecs: Second
  serviceCode: string
  serviceName: string
  allowFlexDelivery: boolean
  flexType: FlexType | null
  internalNameText: string
  internalDescriptionText: string
  externalNameText: string
  externalDescriptionText: string
  serviceMappingType: ServiceMappingType
  stopsBeforeShowEta: number | null
  pickupPreparationTime?: Second
  deliveryPreparationTime?: Second
  minutesUntilDelivery: number | null
  sendAutomaticEtaSms: boolean | null
  serviceDeliveryType: ServiceDeliveryType | null
  allowOffered: boolean
  pickupDurationSecondsApartment?: number | null
  deliveryDurationSecondsApartment?: number | null
}

export type ServiceMappingType = 'service' | 'vas'

export interface ServiceMatrixData extends ImmutableMap<ServiceMapping> {}

type SlotProps = {
  id: SlotIdType
  createdBy: string
  createdByName?: string
  courierId: UnitIdType
  unitName?: string
  unitId: UnitIdType
  state: string
  manuallyRoutePlan: boolean
  partiallyForced?: boolean
  palletRuleOverridden?: boolean
  timeFrom: string
  registrationNumber: null | string
  timeTo: string
  tzTimeFrom: string
  tzTimeTo: string
  color: string
  name: string
  delay: number
  type: string
  departmentId: number
  deptName: string
  driverReturnToAddress: boolean
  startLocationLat: number
  startLocationLng: number
  startLocationAddress?: string
  startLocationPostalCode?: string
  startLocationPostalArea?: string
  startLocationCountryCode?: string
  endLocationLat: number
  endLocationLng: number
  endLocationAddress?: string
  endLocationPostalCode?: string
  endLocationPostalArea?: string
  endLocationCountryCode?: string
  planningCompleted: boolean
  userId?: CourierUserId
  area?: List<Coordinates>
  areaSequence?: ImmutableMap<{ pins: List<Coordinates> }>
  producedInCorrectOrder: boolean | null
  locked?: boolean
  vehicleId: number
  break?: Map<string, string>
  totalSuggestedDistance: number
  driverUserId?: number
  actualDrivenDistanceMeter?: number
  correctlyFinishedConsignments?: number
  finishedConsignments?: number
  netPlannedDistanceMeter?: number
  numberOfOrders?: number
  numberOfConsignments?: number
  numberOfTrips?: number
  plannedDistanceMeter?: number
  plannedEndTime?: string
  plannedStartTime?: string
  plannedTimeSec?: number
  waitingTimeSec?: number
  totalVolumeDmq?: number
  totalWeightKg?: number
  numberOfStops?: number
  totalPalletFootprintsValue?: number
  billingOrderId?: OrderIdType | null
  labelIds: number[]
  skipAutoDispatch?: boolean
  postalCodeRangesSet?: boolean
  vehicleName?: string
  // Whether the stop order came from the optimizer. OPTIMIZATION_FAILED when the optimizer failed and the route was
  // sent to the driver in loading scan order instead, MANUALLY_PLANNED when the planner chose to plan the route without
  // an optimizer, see GLOW-4106
  optimizationStatus?: OptimizationStatus
}

export type OptimizationStatus = 'OPTIMIZED' | 'OPTIMIZATION_FAILED' | 'MANUALLY_PLANNED'

export interface Slot extends ImmutableMap<SlotProps> {}

export interface Coordinates
  extends ImmutableMap<{
    lat: number
    lng: number
  }> {}

export interface SlotProgress
  extends ImmutableMap<{
    id: SlotIdType
    states: List<SlotProgressState>
  }> {}

export interface SlotProgressState
  extends ImmutableMap<{
    orderId: OrderIdType
    state: ConsignmentState
  }> {}

export interface VehicleProps {
  id: VehicleIdType
  userId: number
  updatedAt: string
  updatedBy: string
  createdAt: string
  createdBy: string
  capacity?: number
  speedFactor?: number
  serviceTimeFactor: number
  startLocationLat: number
  startLocationLng: number
  endLocationLat?: number
  endLocationLng?: number
  driverReturnToAddress: boolean
  name: string
  isActive: boolean
  vehicleType: string
  departments: List<DepartmentIdType>
  fuelType: string
  fuelCategory: string
  volumeCapacity: number
  weightCapacity: number
  color?: string
  maxRangeKm?: number
  registrationNumber?: string
  commercialTrafficEligibility?: boolean
  vehicleBrand?: string
  vehicleModel?: string
  vehicleControl?: string
  vehicleControlComment?: string
  euroClass?: string
  co2?: string
  break?: Map<string, string>
  minShipmentWeightKg?: number
  maxShipmentWeightKg?: number
  canCarryPallets?: boolean
}

export interface Vehicle extends ImmutableMap<VehicleProps> {}

export interface UnitProps {
  id: UnitIdType
  userId: number
  updatedAt: string
  updatedBy: string
  createdAt: string
  createdBy: string
  name: string
  alystraId: string | null
  isActive: boolean
  vehicleType: string
  driverReturnToAddress: boolean
  departments: List<DepartmentIdType>
  startLocationLat: number
  startLocationLng: number
  endLocationLat: number
  endLocationLng: number
  serviceTimeFactor: number
  speedFactor: number
  capacity: number
  fuelType: string
  fuelCategory: string
  volumeCapacity: number
  weightCapacity: number
  color: string
  location?: CourierLocation
  latestLogin?: string
  latestBuild?: number
  maxRangeKm: number
  registrationNumber: string
  commercialTrafficEligibility?: boolean
  vehicleBrand: string
  vehicleModel: string
  vehicleControl: string
  vehicleControlComment: string
  euroClass: string
  co2: string
  userIds?: List<CourierUserId>
  users?: Map<number, CourierUser>
  break: Map<string, string>
  vendorSiteId?: number
  costDeptId?: number
  selfBilling?: boolean
  internalTrade?: boolean
}

export interface Unit extends ImmutableMap<UnitProps> {}

export type DefaultVehicleIdType = number

export type DefaultVehicleProps = {
  id: DefaultVehicleIdType
  unitId: UnitIdType
  departmentId: DepartmentIdType
  vehicleId?: VehicleIdType
}

export interface DefaultVehicle extends ImmutableMap<DefaultVehicleProps> {}

type UnitWithoutUsersProps = Omit<UnitProps, 'users'>

export interface UnitWithoutUsers extends ImmutableMap<UnitWithoutUsersProps> {}

export const courierPhoneNumber = (unit: Unit) => {
  const courierUsers = unit.get('userIds')

  return courierUsers && courierUsers.size > 1
    ? i18next.t('courier.multipleDrivers')
    : unit.get('users')?.first(null)?.get('phoneNumber')
}

export const courierDescription = (unit: Unit) => {
  const courierUsers = unit.get('userIds')

  if (courierUsers && courierUsers.size > 1) {
    return i18next.t('courier.multipleDrivers')
  } else {
    const firstUser = unit.get('users')?.first(undefined)
    return `${firstUser?.get('name', '')}, ${firstUser?.get('phoneNumber')}`
  }
}

export type CourierUserId = number

export interface CourierUserProps {
  id: CourierUserId
  name: string
  canLogIn: boolean
  secureLoginEnabled: boolean
  phoneNumber: string
  driverId: string
  unitIds: List<number>
  //device info
  lastLogin: string
  deviceBrand: string
  deviceModel: string
  deviceOs: string
  appBuild: number
  alcoholDeliveryEducation: boolean
  alcoholDeliveryEducationDate: string | null
  alcoholDeliveryEducationValidTo: string | null
  departments: List<DepartmentIdType>
  email: string | null
  dateOfBirth: DateTime | null
  occupationalInjuryInsurance: boolean | null
  courierScanEnabled: boolean
  role: string
}

export interface CourierUser extends ImmutableMap<CourierUserProps> {}

export interface CourierLocationProps {
  id: CourierLocationIdType
  updatedAt: string
  courierId: UnitIdType
  userId: number
  departmentId: DepartmentIdType
  departmentIds?: List<DepartmentIdType>
  custId: CustomerIdType
  customerIds: List<CustomerIdType>
  lat: number
  lng: number
  //optional fields for map markers
  userName?: string
  userPhoneNumber?: string
}

export interface CourierLocation extends ImmutableMap<CourierLocationProps> {}

export interface OrderCorrectionProps {
  id: number
  accepted: boolean
  createdBy: string
  deliveryWaitingTimeInMinutes: number
  numberOfPackages: number
  orderId: number
  pickupWaitingTimeInMinutes: number
  volumeUnit: string
  volumeValue: number
  weightUnit: string
  weightValue: number
  userName: number
  departmentId: DepartmentIdType
  deliveryLoadingTimeInMinutes: number
  pickupLoadingTimeInMinutes: number
  comment: string
}

export type RoleId = number

export interface RoleProps {
  id: RoleId
  name: string
  icon?: IconName
  description?: string
  access: List<UserAccessKey>
  descriptionI18nKey?: string
  nameI18nKey?: string
  defaultForUserRole?: string
}

export interface SessionUser extends ImmutableMap<UserBase> {}

export interface Role extends ImmutableMap<RoleProps> {}

export interface UserProps extends Omit<UserBase, 'customerIds' | 'role' | 'access'> {
  departments?: List<DepartmentIdType>
  customerIds?: List<CustomerIdType>
  roleIds?: List<RoleId>
  role: string
}

export interface User extends ImmutableMap<UserProps> {}

export interface AdminCustomerDeviationProps {
  id: number
  headerEn: string
  deviationCode: string
}

export interface CustomMeasurementProps {
  id: number
  customerId: CustomerIdType
  name: string
  presetDimension: string
  weight: number
  height: number
  length: number
  width: number
}

export interface CustomMeasurement extends ImmutableMap<CustomMeasurementProps> {}

export interface PlannerProps {
  planner: number | null
  name: string
  email?: string
  phoneNumber: string
  departments?: number[]
  customers?: number[]
  roleType: string
  canLogIn: boolean
  roles?: RoleId[]
}

export interface OptimizingProps {
  id: number
  containErrorResponse: boolean
  containRequest: boolean
  containResponse: boolean
  courierId: number | null
  createdAt: string
  departmentname: string
  graphhopperId: string
  requestorname: string
  failed: boolean
  finished: boolean
  errors: List<IError> | null
  status: string
  type: string
  updatedAt: string
  requestByteSize: number
  consignmentIds: Set<number> | null
  numberOfVehicles: number
  processingTime: number
  orderIds: Set<number> | null
  slotIds: Set<number>
  slotNames: Set<string>
}

export type Optimizing = ImmutableMap<OptimizingProps>

export type ApolloAuditLog = ImmutableMap<{
  id: number
  departmentId: DepartmentIdType
  departmentName: string
  // null for entries written before Glow recorded when the work finished
  processedAt: string | null
  routeId: number
  routeName: string
  status: string
  timestamp: string
  type: string
  userId: number
  userName: string | null
}>

export type ApolloAuditLogDetails = ImmutableMap<ApolloAuditLogDetailsProps>

export type ApolloAuditLogDetailsProps = {
  id: number
  departmentId: DepartmentIdType
  departmentName: string
  processedAt: string | null
  request: AnyData
  response: AnyData
  routeId: number
  routeName: string
  status: string
  timestamp: string
  type: string
  userId: number
  userName: string | null
}

export interface TemplateProps {
  id: number
  departmentId: DepartmentIdType
  name: string
}

export type Template = ImmutableMap<TemplateProps>

export interface TemplateSlotProps {
  id: number
  templateId: number
  name: string
}

export type TemplateSlot = ImmutableMap<TemplateSlotProps>

export type DepartmentGroupIdType = number

export type CustomerGroupIdType = number

export interface DepartmentGroupProps {
  id: DepartmentGroupIdType
  name: string
  departments: List<DepartmentIdType>
  description?: string
  type: DepartmentGroupType
}

export interface DepartmentGroupPropsJS {
  id: DepartmentGroupIdType
  name: string
  departments: DepartmentIdType[]
  description?: string
  type?: DepartmentGroupType
}

export interface DepartmentGroup extends ImmutableMap<DepartmentGroupProps> {}

export interface CustomerGroupProps {
  id: CustomerGroupIdType
  name: string
  customers: List<CustomerIdType>
  description?: string
  usedInPreadvice: boolean
}

export interface CustomerGroup extends ImmutableMap<CustomerGroupProps> {}

export interface ConsignmentEventLM
  extends ImmutableMap<{
    id: ConsignmentEventId
    consignmentId: ConsignmentIdType
    type: EventType
    eventTime: ISODateTimeString
    data?: AnyData
    packageId?: string
    shipmentId?: string
    orderId?: OrderIdType
    syncJobId?: number
    lmPayload?: AnyData
    sentToLmAt?: ISODateTimeString
  }> {}

export type PreAdvicePolicyIdType = number

export type PreAdvicePolicyRowIdType = number

export interface PreAdvicePolicyProps {
  id: PreAdvicePolicyIdType
  name: string
  servicePolicies: List<PreAdvicePolicyRowIdType>
  departmentGroups: List<DepartmentGroupIdType>
  customerGroups: List<CustomerGroupIdType>
  customers: List<CustomerIdType>
  departments: List<DepartmentIdType>
  description?: string
}

export interface PreAdvicePolicyPropsJS {
  id: PreAdvicePolicyIdType
  name: string
  servicePolicies: PreAdvicePolicyRowIdType[]
  departmentGroups: DepartmentGroupIdType[]
  customerGroups: CustomerGroupIdType[]
  customers: CustomerIdType[]
  departments: DepartmentIdType[]
  description?: string
}

type ServicePolicyIdType = string
export type PreadviceSchemeType = 'PULL' | 'PUSH' | 'NONE'
export type ApplyToPreadviceOrders = 'WITH_DATE' | 'WITHOUT_DATE' | 'WITH_AND_WITHOUT_DATE'

export interface PreAdviceServicePolicyProps {
  id: ServicePolicyIdType | null
  serviceCode: string
  vasCode?: string
  sameDay: boolean
  triggerEvent: string
  cutoff: string
  relativeCutoffMinutes?: string
  calendarDays: number
  returnToSenderAfterDays?: number
  scheme: PreadviceSchemeType | null
  reminderSmsHours?: number
  preAdviceAttempts?: number
  reminderSmsToSenderHours?: number
  returnToPup?: boolean
  enableCancelBookingTimes?: boolean
  deliveryAttempts?: number
  applyToOrders?: ApplyToPreadviceOrders
}

export type ExportDataType =
  | 'SHIPMENT_EXPORT'
  | 'COURIER_WORK_REPORT'
  | 'COURIER_WORK_REPORT_EXTENDED'
  | 'END_OF_DAY_REPORT'
  | 'LOCATION_LIST'
  | 'SOCIAL_CONTROL'
  | 'ORDER_EXPORT'
  | 'ORDER_EXPORT_ANONYMOUS'
  | 'ORDER_DEVIATION_EXPORT'
  | 'ORDER_PARTNER_EXPORT'
  | 'DEVIATION_EVENTS'
  | 'OPTIMIZATION_RESULTS'
  | 'FINANCIAL_REPORT'

export interface ExportDataExecution
  extends ImmutableMap<{
    id: number
    createdAt: string
    updatedAt: string
    createdBy: string
    startedAt: string | null
    departmentId: DepartmentIdType | undefined
    departmentGroupId: DepartmentGroupIdType | undefined
    errorMessage: string | undefined
    failed: boolean
    finished: boolean
    finishedDays: number
    totalDays: number
    fromDate: string
    toDate: string
    name: string
    type: ExportDataType
    updatedByName: string
  }> {}

export interface ImportDataExecution
  extends ImmutableMap<{
    id: number
    createdAt: string
    departmentId: DepartmentIdType
    importId: string
    importedByName?: string
    fileName: string
    numberOfOrders: number
  }> {}

interface OrderPreAdviceProps {
  consignmentId: number
  orderId: number
  preAdviceStatus: string
  hasPreAdviceSetting: boolean
  type: string | null
  policyName?: string
  triggerEvent?: string
  serviceDeliveryType?: ServiceDeliveryType
  policyId?: number
  cutoff?: ISOTimeString
}

export interface OrderPreadvice extends ImmutableMap<OrderPreAdviceProps> {}

export interface DeliveryMetaDataProps {
  id: DeliveryMetaDataId
  consignmentId: ConsignmentIdType
  messageText?: string
  textId?: TextIdType
  lockCode?: string
  housingInformation?: HousingInformation
}

export interface DeliveryMetaData extends ImmutableMap<DeliveryMetaDataProps> {}

export interface InvoicingStatusCountProps {
  departmentId: DepartmentIdType
  fromDate: string
  toDate: string
  toBeInvoiced: number
  invoicingFailed: number
  correctionPending: number
  invoicingPaused: number
  zeroPrice: number
  awaitingAcknowledgement: number
}

export interface InvoicingStatusCount extends ImmutableMap<InvoicingStatusCountProps> {}

export interface InvoiceItemProps {
  id: number
  orderId: OrderIdType
  createdAt: Date | null
  articleCode: string | null
  specification: string
  quantity: number | string | null
  customerAmount: number | string | null
  carrierAmount: number | string | null
  lineId: string | null
  unitId: UnitIdType | null
  customerPerUnitPrice: number | string | null
  carrierPerUnitPrice: number | string | null
}

export interface InvoiceItem extends ImmutableMap<InvoiceItemProps> {}

export type PreAdviceStatusType =
  | 'ERROR'
  | 'NO_DELIVERY_TIME_WINDOW'
  | 'NO_VALID_PHONE_OR_EMAIL'
  | 'WAITING_FOR_PACKAGES'
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'FINISHED'
  | 'PRE_ADVICE_LOCKED'

export type PreAdviceType = 'PULL' | 'PUP' | 'PUSH' | 'RETURN'

export interface PreAdviceProps {
  arrivedAtHub: boolean | null
  consignmentId: ConsignmentIdType
  departmentId: DepartmentIdType
  failed: boolean
  hasPreAdviceSetting: boolean
  orderId: OrderIdType
  preAdviceScheduledDate: ISODateString
  preAdviceStatus: PreAdviceStatusType
  preAdvices: number
  returnToSender: boolean
  type: PreAdviceType | null
}

export interface PreAdvice extends ImmutableMap<PreAdviceProps> {}

export interface MultistopDetailProps {
  orderId: OrderIdType
  stopOrder: number
  groupId: string
  shipmentId: string
  deliveryTime?: ISODateTimeString
  stopType: 'delivery' | 'pickup'
  departmentId: DepartmentIdType
  stopAddress?: string
  stopZipCode?: string
  stopZipArea?: string
  stopCountry?: string
}

export interface MultistopDetail extends ImmutableMap<MultistopDetailProps> {}

export interface IntegrationExplanationProps {
  sentTo: string
  comment: string
  succeeded: boolean
}

export interface IntegrationExplanation extends ImmutableMap<IntegrationExplanationProps> {}

export interface InvoicingResponseProps {
  integrationExplanations: List<IntegrationExplanation>

  [key: string]: any
}

export interface InvoicingResponse extends ImmutableMap<InvoicingResponseProps> {}

export interface InvoicingDataProps {
  orderId: OrderIdType
  status: boolean
  response: InvoicingResponse

  [key: string]: any
}

export interface InvoicingData extends ImmutableMap<InvoicingDataProps> {}

export type ServiceDeliveryType = 'Delivery' | 'Return' | 'PickupPoint' | 'Pickup'

export interface PriceUpdateProps {
  id: number
  orderId: OrderIdType
  netCustomerPrice: number | null
  netResourcePrice: number | null
  customerCurrency: string | null
  resourceCurrency: string | null
}

export interface PriceUpdate extends ImmutableMap<PriceUpdateProps> {}
